import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { qrCodeService } from '../../../utils/storage';
import { useToast } from '../../../hooks/useToast';
import { useSubscription } from '../../../hooks/useSubscription';

interface QRCodeModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function QRCodeModal({ onClose, onSuccess }: QRCodeModalProps) {
  const { t } = useTranslation('dashboard');
  const { showToast } = useToast();
  const { canCreateQR } = useSubscription();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [color, setColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [size, setSize] = useState(300);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (url) {
      generateQRCode();
    }
  }, [url, color, bgColor, size]);

  const generateQRCode = async () => {
    try {
      const qrUrl = await QRCode.toDataURL(url, {
        width: size,
        margin: 2,
        color: {
          dark: color,
          light: bgColor,
        },
      });
      setQrCodeUrl(qrUrl);
    } catch (err) {
      console.error('QR 코드 생성 실패:', err);
    }
  };

  const handleSave = async () => {
    if (!name || !url) {
      showToast('이름과 URL을 모두 입력해주세요', 'error');
      return;
    }

    if (!canCreateQR()) {
      showToast('QR 코드 생성 한도에 도달했습니다. 프로 플랜으로 업그레이드하세요.', 'error');
      return;
    }

    setLoading(true);
    try {
      await qrCodeService.create({
        name,
        url,
        qr_image: qrCodeUrl,
      });

      showToast('QR 코드가 생성되었습니다', 'success');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('QR 코드 저장 실패:', error);
      showToast(error.message || 'QR 코드 생성에 실패했습니다', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">{t('qr.create')}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('qr.name')}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="예: 메뉴판 QR"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('qr.url')}
                </label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="https://example.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('qr.color')}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('qr.bgColor')}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('qr.size')}: {size}px
                </label>
                <input
                  type="range"
                  min="200"
                  max="500"
                  value={size}
                  onChange={(e) => setSize(Number(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>

            {/* Preview */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('qr.preview')}
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 flex items-center justify-center bg-gray-50">
                {qrCodeUrl ? (
                  <img src={qrCodeUrl} alt="QR Code Preview" className="max-w-full" />
                ) : (
                  <div className="text-center text-gray-400">
                    <i className="ri-qr-code-line text-6xl mb-2 block"></i>
                    <p className="text-sm">URL을 입력하면 미리보기가 표시됩니다</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              {t('action.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-6 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <i className="ri-loader-4-line animate-spin"></i>}
              {loading ? '생성 중...' : t('action.create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
